import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function AdminSponsors({ sponsors, refresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const del = async (s) => {
    if (!confirm(`Delete sponsor "${s.name}"?`)) return;
    try {
      await api.delete(`/admin/sponsors/${s.id}`);
      toast.success("Sponsor deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/sponsors/${s.id}`, { ...s, active: !s.active });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-sponsors-section">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-neutral-400">{sponsors.length} sponsors</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={() => { setEditing(null); setOpen(true); }} className="bg-svn-red text-white font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-red-700 inline-flex items-center gap-2" data-testid="admin-new-sponsor-btn">
              <Plus size={14} /> New Sponsor
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F0F0F] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editing ? "Edit Sponsor" : "New Sponsor"}</DialogTitle>
            </DialogHeader>
            <SponsorForm sponsor={editing} onSaved={() => { setOpen(false); refresh(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="admin-sponsors-list">
        {sponsors.map((s) => (
          <div key={s.id} className={`bg-[#0F0F0F] border p-5 ${s.active === false ? "border-white/5 opacity-60" : "border-white/10"}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <p className="font-display text-xl font-bold text-gold">{s.name}</p>
                <p className="text-xs text-neutral-500 uppercase tracking-widest mt-1">
                  {s.category || "—"} · {s.placement || "home"}
                </p>
                <p className="text-sm text-neutral-400 mt-2 line-clamp-2">{s.description}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button onClick={() => { setEditing(s); setOpen(true); }} className="text-neutral-400 hover:text-gold" data-testid={`edit-sponsor-${s.id}`}><Pencil size={14} /></button>
                <button onClick={() => del(s)} className="text-neutral-400 hover:text-svn-red" data-testid={`delete-sponsor-${s.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => toggleActive(s)}
                data-testid={`toggle-active-${s.id}`}
                className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 ${s.active === false ? "bg-neutral-800 text-neutral-400" : "bg-svn-red text-white"}`}
              >
                {s.active === false ? "Inactive" : "Active"}
              </button>
              {s.featured && (
                <span className="text-[10px] font-bold uppercase tracking-widest px-3 py-1 bg-gold text-black">Featured</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SponsorForm({ sponsor, onSaved }) {
  const [f, setF] = useState({
    name: sponsor?.name || "",
    logo_url: sponsor?.logo_url || "",
    website_url: sponsor?.website_url || "",
    description: sponsor?.description || "",
    category: sponsor?.category || "",
    placement: sponsor?.placement || "both",
    active: sponsor?.active ?? true,
    featured: sponsor?.featured ?? true,
    order: sponsor?.order ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (sponsor) await api.put(`/admin/sponsors/${sponsor.id}`, f);
      else await api.post("/admin/sponsors", f);
      toast.success("Sponsor saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const I = (k, label, type = "text") => (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">{label}</label>
      <input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: type === "number" ? Number(e.target.value) : e.target.value })} data-testid={`sponsor-form-${k}`} className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold" />
    </div>
  );

  return (
    <form onSubmit={save} className="space-y-3" data-testid="sponsor-form">
      <div className="grid md:grid-cols-2 gap-3">
        {I("name", "Name")}
        {I("category", "Category (e.g., Food, Travel, Tech)")}
      </div>
      {I("logo_url", "Logo URL")}
      {I("website_url", "Website URL")}
      <div>
        <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Description</label>
        <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="sponsor-form-description" className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold" />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Placement</label>
          <select value={f.placement} onChange={(e) => setF({ ...f, placement: e.target.value })} data-testid="sponsor-form-placement" className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white">
            <option value="home">Home only</option>
            <option value="sponsors">Sponsors page only</option>
            <option value="both">Both</option>
            <option value="none">Hidden</option>
          </select>
        </div>
        {I("order", "Order", "number")}
        <label className="flex items-center gap-3 text-sm text-neutral-300 mt-7">
          <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="accent-yellow-400" data-testid="sponsor-form-active" />
          Active
        </label>
      </div>
      <label className="flex items-center gap-3 text-sm text-neutral-300">
        <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} className="accent-yellow-400" data-testid="sponsor-form-featured" />
        Featured (gets premium placement)
      </label>
      <button type="submit" disabled={saving} data-testid="sponsor-form-save" className="bg-svn-red text-white font-black uppercase tracking-widest text-sm px-6 py-3 hover:bg-red-700 disabled:opacity-60">
        {saving ? "Saving…" : "Save Sponsor"}
      </button>
    </form>
  );
}
